import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { api } from "../../lib/api";

const NAV = [
  { label: "About", to: "/about" },
  { label: "Ventures", to: "/ventures", key: "ventures" },
  { label: "Solutions", to: "/solutions", key: "solutions" },
  { label: "Partnerships", to: "/partnerships" },
  { label: "Contact", to: "/contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [ventures, setVentures] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [config, setConfig] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    api.get("/content/ventures").then((r) => setVentures(r.data)).catch(() => {});
    api.get("/content/solutions").then((r) => setSolutions(r.data)).catch(() => {});
    api.get("/content/config").then((r) => setConfig(r.data)).catch(() => {});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => { setOpen(false); navigate(to); };

  const dropdown = (key) =>
    key === "ventures"
      ? ventures.map((v) => ({ label: v.name, to: `/ventures/${v.slug}`, desc: v.tagline }))
      : solutions.map((s) => ({ label: s.title, to: `/solutions/${s.slug}`, desc: s.summary }));

  return (
    <header data-testid="site-header" className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${scrolled || open ? "border-b border-white/[0.07] bg-midnight/85 backdrop-blur-xl" : "bg-transparent"}`}>
      <div className="container-ek flex h-[76px] items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3" onClick={() => setOpen(false)}>
          {config.logo_url ? (
            <span className="rounded-xl bg-white px-2.5 py-1.5 shadow-[0_4px_18px_rgba(9,15,36,0.35)]">
              <img src={config.logo_url} alt="Ekavrix Tech Ventures logo" className="h-9 w-auto max-w-[150px] object-contain" />
            </span>
          ) : (
            <>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-violet to-teal font-display text-lg font-bold text-white">E</span>
              <span className="font-display text-xl font-bold tracking-tight text-mist">{config.wordmark || "Ekavrix"}</span>
            </>
          )}
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {NAV.map((item) =>
            item.key ? (
              <div key={item.label} className="group relative">
                <Link to={item.to} data-testid={`nav-${item.key}`} className="flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-white">
                  {item.label}
                  <ChevronDown size={14} className="transition-transform duration-300 group-hover:rotate-180" />
                </Link>
                <div className="invisible absolute left-1/2 top-full w-[340px] -translate-x-1/2 pt-3 opacity-0 transition-[opacity,visibility] duration-300 group-hover:visible group-hover:opacity-100">
                  <div className="rounded-2xl border border-white/[0.08] bg-deep/95 p-2 shadow-2xl backdrop-blur-xl">
                    {dropdown(item.key).map((d) => (
                      <Link key={d.to} to={d.to} data-testid={`nav-drop-${d.to.split("/").pop()}`} className="block rounded-xl px-4 py-3 transition-colors hover:bg-white/[0.05]">
                        <span className="block font-display text-sm font-semibold text-mist">{d.label}</span>
                        <span className="mt-0.5 block line-clamp-1 text-xs text-slate-400">{d.desc}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link key={item.label} to={item.to} data-testid={`nav-${item.label.toLowerCase()}`} className="rounded-full px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:text-white">
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/contact" data-testid="nav-lets-talk" className="btn-primary hidden !px-6 !py-2.5 sm:inline-flex">
            Let's Talk <ArrowRight size={15} />
          </Link>
          <button data-testid="nav-mobile-toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open}
            onClick={() => setOpen(!open)} className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-mist lg:hidden">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="border-t border-white/[0.06] bg-midnight/95 backdrop-blur-xl lg:hidden">
          <div className="container-ek flex flex-col gap-1 py-6">
            {NAV.map((item) => (
              <div key={item.label}>
                <button onClick={() => go(item.to)} data-testid={`nav-mobile-${item.label.toLowerCase()}`} className="w-full rounded-xl px-4 py-3 text-left font-display text-lg font-semibold text-mist transition-colors hover:bg-white/[0.05]">
                  {item.label}
                </button>
                {item.key && (
                  <div className="mb-2 ml-4 border-l border-white/10 pl-4">
                    {dropdown(item.key).map((d) => (
                      <button key={d.to} onClick={() => go(d.to)} className="block w-full py-2 text-left text-sm text-slate-400 transition-colors hover:text-teal">
                        {d.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <button onClick={() => go("/contact")} data-testid="nav-mobile-lets-talk" className="btn-primary mt-4 w-full">
              Let's Talk <ArrowRight size={15} />
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
